import type { PathTileAtlasId } from '../pathArtStyles';
import { getPathTileArtStyle } from '../pathArtStyles';
import type { PathTileNeighborStatus } from './PathAutotiler';
import { composePathTileLayers, createCanvas, hashSeed, seededFloat } from './pathTileComposer';
import { fillMaskedCanvasWithTexture } from './pathTextureFill';

type Quadrant = 'northWest' | 'northEast' | 'southWest' | 'southEast';

interface SquarePathShape {
  readonly empty?: boolean;
  readonly isolated?: boolean;
  readonly north: boolean;
  readonly east: boolean;
  readonly south: boolean;
  readonly west: boolean;
  readonly northWest: boolean;
  readonly northEast: boolean;
  readonly southEast: boolean;
  readonly southWest: boolean;
}

interface QuarterRecipe {
  readonly archetype: SquareQuarterArchetype;
  readonly rotationQuarterTurns: number;
  readonly flipHorizontal: boolean;
}

type QuarterLayer = 'outer' | 'inner';
type SquareQuarterArchetype = 0 | 1 | 2 | 3 | 4;

const EMPTY_SHAPE: SquarePathShape = {
  empty: true,
  north: false,
  east: false,
  south: false,
  west: false,
  northWest: false,
  northEast: false,
  southEast: false,
  southWest: false,
};

const ISOLATED_SHAPE: SquarePathShape = {
  isolated: true,
  north: false,
  east: false,
  south: false,
  west: false,
  northWest: false,
  northEast: false,
  southEast: false,
  southWest: false,
};

const SHARED_QUARTER_RECIPES: readonly [number, number, boolean][] = [
  [0, 0, false], [0, 0, false], [1, 0, false], [1, 0, false], [1, 1, false], [2, 0, false], [3, 0, false], [4, 0, false],
  [0, 0, false], [0, 0, false], [1, 0, false], [1, 0, false], [1, 1, false], [1, 1, false], [3, 0, false], [4, 0, false],
  [0, 0, true], [1, 0, true], [0, 0, true], [1, 0, true], [0, 0, true], [1, 0, true], [0, 0, true], [1, 0, true],
  [1, 1, false], [3, 0, true], [4, 0, false], [4, 0, false], [1, 1, false], [3, 0, true], [1, 1, false], [4, 0, false],
  [0, 2, true], [1, 3, false], [0, 2, true], [1, 3, false], [0, 2, true], [1, 3, false], [0, 2, true], [1, 3, false],
  [1, 0, false], [3, 2, true], [1, 0, false], [3, 2, true], [1, 0, false], [4, 0, false], [1, 0, false], [4, 0, false],
  [0, 2, false], [0, 2, false], [1, 3, false], [1, 3, false], [1, 0, true], [1, 0, true], [3, 2, false], [3, 2, false],
  [0, 2, false], [0, 2, false], [1, 3, false], [1, 3, false], [1, 0, true], [1, 0, true], [4, 0, false], [4, 0, false],
] as const;

// Fixed brush indices preserve the legacy square preview atlas ordering.
const SQUARE_BRUSH_SHAPES: readonly SquarePathShape[] = [
  { north: true, east: true, south: false, west: true, northWest: false, northEast: true, southEast: false, southWest: false },
  { north: true, east: true, south: false, west: true, northWest: false, northEast: false, southEast: false, southWest: false },
  { north: true, east: true, south: true, west: true, northWest: false, northEast: false, southEast: false, southWest: false },
  { north: true, east: true, south: true, west: true, northWest: false, northEast: true, southEast: false, southWest: true },
  { north: true, east: true, south: true, west: true, northWest: true, northEast: true, southEast: true, southWest: false },
  { north: true, east: true, south: false, west: true, northWest: true, northEast: true, southEast: false, southWest: false },
  { north: true, east: false, south: false, west: true, northWest: true, northEast: false, southEast: false, southWest: false },
  { north: true, east: false, south: true, west: false, northWest: false, northEast: false, southEast: false, southWest: false },
  { north: true, east: false, south: false, west: true, northWest: true, northEast: false, southEast: false, southWest: false },
  { north: false, east: true, south: true, west: true, northWest: false, northEast: false, southEast: false, southWest: false },
  { north: false, east: true, south: false, west: false, northWest: false, northEast: false, southEast: false, southWest: false },
  EMPTY_SHAPE,
  { north: true, east: false, south: false, west: true, northWest: false, northEast: false, southEast: false, southWest: false },
  EMPTY_SHAPE,
  ISOLATED_SHAPE,
  { north: true, east: true, south: true, west: true, northWest: true, northEast: true, southEast: true, southWest: false },
] as const;

const quarterPieceCache = new Map<string, HTMLCanvasElement>();
const transformedQuarterCache = new Map<string, HTMLCanvasElement>();
const composedTileCache = new Map<string, HTMLCanvasElement>();
const quarterAtlasCache = new Map<string, HTMLCanvasElement>();
const brushAtlasCache = new Map<string, HTMLCanvasElement>();

const CANONICAL_QUARTER_SIZE = 32;
const OUTER_CORNER_ARCHETYPE: SquareQuarterArchetype = 0;
const STRAIGHT_ARCHETYPE: SquareQuarterArchetype = 1;
const INNER_CORNER_ARCHETYPE: SquareQuarterArchetype = 2;
const INVERSE_INNER_CORNER_ARCHETYPE: SquareQuarterArchetype = 3;
const SOLID_ARCHETYPE: SquareQuarterArchetype = 4;

function thresholdCanvasAlpha(canvas: HTMLCanvasElement, alphaCutoff = 127): void {
  const context = canvas.getContext('2d');
  if (!context) {
    return;
  }
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  const data = image.data;
  for (let index = 0; index < data.length; index += 4) {
    data[index + 3] = data[index + 3] > alphaCutoff ? 255 : 0;
  }
  context.putImageData(image, 0, 0);
}

function createTransparentCanvas(size: number): HTMLCanvasElement {
  return createCanvas(size, size);
}

function drawQuarterShape(
  context: CanvasRenderingContext2D,
  color: string,
  size: number,
  archetype: SquareQuarterArchetype,
  borderWidthPx: number,
): void {
  const border = borderWidthPx <= 0
    ? 0
    : Math.max(1, Math.round((size / CANONICAL_QUARTER_SIZE) * borderWidthPx));
  const half = size / 2;
  const diagonalInset = border * 1.5;

  context.fillStyle = color;
  context.beginPath();

  switch (archetype) {
    case OUTER_CORNER_ARCHETYPE: {
      const radius = Math.max(0, half - border);
      context.moveTo(size, size - radius);
      context.arc(size, size, radius, -Math.PI / 2, Math.PI, true);
      context.lineTo(size, size);
      context.closePath();
      break;
    }
    case STRAIGHT_ARCHETYPE: {
      context.rect(half + border, 0, half - border, size);
      break;
    }
    case INNER_CORNER_ARCHETYPE: {
      const curveY = half + diagonalInset;
      context.moveTo(0, curveY);
      context.lineTo(0, size);
      context.lineTo(size, size);
      context.lineTo(size, curveY);
      context.arc(half, curveY, half, 0, Math.PI, true);
      context.closePath();
      break;
    }
    case INVERSE_INNER_CORNER_ARCHETYPE: {
      const cutRadius = border > 0 ? half + border : half + diagonalInset;
      context.moveTo(cutRadius, 0);
      context.lineTo(size, 0);
      context.lineTo(size, size);
      context.lineTo(0, size);
      context.lineTo(0, cutRadius);
      context.arc(0, 0, cutRadius, Math.PI / 2, 0, true);
      context.closePath();
      break;
    }
    case SOLID_ARCHETYPE: {
      context.rect(0, 0, size, size);
      break;
    }
    default:
      break;
  }

  context.fill();
}

function proceduralQuarterPiece(
  atlasId: PathTileAtlasId,
  size: number,
  archetype: SquareQuarterArchetype,
  layer: QuarterLayer,
): HTMLCanvasElement {
  const cacheKey = `${atlasId}:${size}:${archetype}:${layer}`;
  const existing = quarterPieceCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const style = getPathTileArtStyle(atlasId);
  if (layer === 'outer' && !style.borderColor) {
    return createTransparentCanvas(size);
  }
  const canvas = createCanvas(size, size);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, size, size);
  const color = layer === 'outer' ? style.borderColor ?? style.fillColor : style.fillColor;
  const borderWidth = layer === 'inner' ? style.borderWidthPx : 0;
  drawQuarterShape(context, color, size, archetype, borderWidth);
  thresholdCanvasAlpha(canvas);

  quarterPieceCache.set(cacheKey, canvas);
  return canvas;
}

function composeSquareQuarterPiece(
  atlasId: PathTileAtlasId,
  size: number,
  archetype: SquareQuarterArchetype,
  textureImage?: HTMLImageElement,
): HTMLCanvasElement {
  const hasTexture = textureImage && textureImage.complete;
  const cacheKey = `${atlasId}:${size}:${archetype}:combined-quarter:${hasTexture ? 'tex' : 'notex'}`;
  const existing = quarterPieceCache.get(cacheKey);
  if (existing) {
    return existing;
  }
  const canvas = createCanvas(size, size);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }
  context.imageSmoothingEnabled = false;
  context.drawImage(proceduralQuarterPiece(atlasId, size, archetype, 'outer'), 0, 0);
  const innerPiece = proceduralQuarterPiece(atlasId, size, archetype, 'inner');
  if (hasTexture) {
    const texturedInner = createCanvas(size, size);
    const texturedInnerContext = texturedInner.getContext('2d');
    if (texturedInnerContext) {
      texturedInnerContext.imageSmoothingEnabled = false;
      texturedInnerContext.drawImage(innerPiece, 0, 0);
      fillMaskedCanvasWithTexture(atlasId, texturedInner, innerPiece, textureImage);
      context.drawImage(texturedInner, 0, 0);
    } else {
      context.drawImage(innerPiece, 0, 0);
    }
  } else {
    context.drawImage(innerPiece, 0, 0);
  }
  quarterPieceCache.set(cacheKey, canvas);
  return canvas;
}

function transformQuarterCanvas(
  source: HTMLCanvasElement,
  recipeKey: string,
  rotationQuarterTurns: number,
  flipHorizontal: boolean,
): HTMLCanvasElement {
  if (rotationQuarterTurns === 0 && !flipHorizontal) {
    return source;
  }
  const cacheKey = `${recipeKey}:${rotationQuarterTurns}:${flipHorizontal ? 1 : 0}`;
  const existing = transformedQuarterCache.get(cacheKey);
  if (existing) {
    return existing;
  }
  const canvas = createCanvas(source.width, source.height);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }
  context.imageSmoothingEnabled = false;
  context.save();
  context.translate(source.width / 2, source.height / 2);
  if (rotationQuarterTurns !== 0) {
    context.rotate(rotationQuarterTurns * (Math.PI / 2));
  }
  context.scale(flipHorizontal ? -1 : 1, 1);
  context.drawImage(source, -source.width / 2, -source.height / 2);
  context.restore();
  transformedQuarterCache.set(cacheKey, canvas);
  return canvas;
}

function encodeCornerMask(
  topLeft: boolean,
  topRight: boolean,
  bottomLeft: boolean,
  bottomRight: boolean,
): number {
  return Number(topLeft)
    | (Number(topRight) << 1)
    | (Number(bottomLeft) << 2)
    | (Number(bottomRight) << 3);
}

function canonicalQuarterTileIndex(quadrant: Quadrant, mask: number): number {
  switch (quadrant) {
    case 'northWest':
      return mask;
    case 'northEast':
      return mask + 16;
    case 'southWest':
      return mask + 32;
    case 'southEast':
      return mask + 48;
  }
}

function getQuarterRecipe(quadrant: Quadrant, mask: number): QuarterRecipe {
  const [archetype, rotationQuarterTurns, flipHorizontal] = SHARED_QUARTER_RECIPES[canonicalQuarterTileIndex(quadrant, mask)];
  return { archetype: archetype as SquareQuarterArchetype, rotationQuarterTurns, flipHorizontal };
}

function resolveQuarterImage(
  atlasId: PathTileAtlasId,
  quarterSize: number,
  quadrant: Quadrant,
  mask: number,
  layer: QuarterLayer,
): HTMLCanvasElement {
  const recipe = getQuarterRecipe(quadrant, mask);
  const recipeKey = `${atlasId}:${quarterSize}:${recipe.archetype}:${layer}`;
  const source = proceduralQuarterPiece(atlasId, quarterSize, recipe.archetype, layer);
  return transformQuarterCanvas(source, recipeKey, recipe.rotationQuarterTurns, recipe.flipHorizontal);
}

function applyCanvasAlphaClip(
  context: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
): boolean {
  const maskContext = source.getContext('2d');
  if (!maskContext) {
    return false;
  }
  const image = maskContext.getImageData(0, 0, source.width, source.height);
  const data = image.data;
  context.beginPath();
  for (let y = 0; y < source.height; y += 2) {
    let runStart = -1;
    for (let x = 0; x < source.width; x += 1) {
      const alpha = data[((y * source.width) + x) * 4 + 3];
      if (alpha > 0 && runStart < 0) {
        runStart = x;
      } else if ((alpha === 0 || x === source.width - 1) && runStart >= 0) {
        const runEnd = alpha > 0 && x === source.width - 1 ? x + 1 : x;
        context.rect(runStart, y, runEnd - runStart, 2);
        runStart = -1;
      }
    }
  }
  context.clip();
  return true;
}

function drawFlowerDecoration(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  seed: string,
  count: number,
  bloomColors: readonly string[],
  centerColor: string,
  stemColor: string,
): void {
  for (let index = 0; index < count; index += 1) {
    const x = width * (0.16 + (seededFloat(`${seed}:x:${index}`) * 0.68));
    const y = height * (0.18 + (seededFloat(`${seed}:y:${index}`) * 0.62));
    const scale = 0.55 + (seededFloat(`${seed}:s:${index}`) * 0.5);
    const petalRadius = Math.max(1.4, width * 0.03 * scale);
    const bloomColor = bloomColors[index % bloomColors.length] ?? bloomColors[0] ?? '#fff';
    context.strokeStyle = stemColor;
    context.lineWidth = Math.max(1, width * 0.012 * scale);
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(x, y + petalRadius * 3.2);
    context.lineTo(x + width * 0.01, y + petalRadius * 1.4);
    context.stroke();
    context.fillStyle = bloomColor;
    for (let petal = 0; petal < 4; petal += 1) {
      const angle = (petal * Math.PI) / 2;
      context.beginPath();
      context.arc(
        x + Math.cos(angle) * petalRadius * 1.35,
        y + Math.sin(angle) * petalRadius * 1.35,
        petalRadius,
        0,
        Math.PI * 2,
      );
      context.fill();
    }
    context.fillStyle = centerColor;
    context.beginPath();
    context.arc(x, y, petalRadius * 0.7, 0, Math.PI * 2);
    context.fill();
  }
}

function applyTileDecorations(
  atlasId: PathTileAtlasId,
  target: HTMLCanvasElement,
  clipMask: HTMLCanvasElement,
  seed: string,
): void {
  const decoration = getPathTileArtStyle(atlasId).decoration;
  if (!decoration) {
    return;
  }
  const context = target.getContext('2d');
  if (!context) {
    return;
  }
  context.save();
  if (!applyCanvasAlphaClip(context, clipMask)) {
    context.restore();
    return;
  }
  if (decoration.kind === 'flowers') {
    drawFlowerDecoration(
      context,
      target.width,
      target.height,
      seed,
      decoration.count,
      decoration.bloomColors,
      decoration.centerColor,
      decoration.stemColor,
    );
  }
  context.restore();
}

function cornerMasksFromShape(shape: SquarePathShape): Record<Quadrant, number> {
  return {
    northWest: encodeCornerMask(shape.northWest, shape.north, shape.west, true),
    northEast: encodeCornerMask(shape.north, shape.northEast, true, shape.east),
    southWest: encodeCornerMask(shape.west, true, shape.southWest, shape.south),
    southEast: encodeCornerMask(true, shape.east, shape.south, shape.southEast),
  };
}

export function createSquarePathShapeFromNeighborStatus(signature: PathTileNeighborStatus): SquarePathShape {
  return {
    north: signature.north,
    east: signature.east,
    south: signature.south,
    west: signature.west,
    northWest: signature.northWest,
    northEast: signature.northEast,
    southEast: signature.southEast,
    southWest: signature.southWest,
  };
}

export function resolveSquarePathBrushShape(tileIndex: number): SquarePathShape | null {
  if (!Number.isFinite(tileIndex)) {
    return null;
  }
  return SQUARE_BRUSH_SHAPES[Math.trunc(tileIndex)] ?? null;
}

export function composeSquarePathTileFromCornerMasks(
  atlasId: PathTileAtlasId,
  tileSize: number,
  masks: Record<Quadrant, number>,
  textureImage?: HTMLImageElement,
): HTMLCanvasElement {
  const hasTexture = textureImage && textureImage.complete;
  const cacheKey = `${atlasId}:${tileSize}:${masks.northWest}:${masks.northEast}:${masks.southWest}:${masks.southEast}:${hasTexture ? 'tex' : 'notex'}`;
  const existing = composedTileCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const canvas = createCanvas(tileSize, tileSize);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }
  const innerCanvas = createCanvas(tileSize, tileSize);
  const innerContext = innerCanvas.getContext('2d');
  if (!innerContext) {
    return canvas;
  }
  const quarterSize = tileSize / 2;
  const outerCanvas = createCanvas(tileSize, tileSize);
  const outerContext = outerCanvas.getContext('2d');
  if (!outerContext) {
    return canvas;
  }
  context.imageSmoothingEnabled = false;
  innerContext.imageSmoothingEnabled = false;
  outerContext.imageSmoothingEnabled = false;
  outerContext.drawImage(resolveQuarterImage(atlasId, quarterSize, 'northWest', masks.northWest, 'outer'), 0, 0, quarterSize, quarterSize);
  outerContext.drawImage(resolveQuarterImage(atlasId, quarterSize, 'northEast', masks.northEast, 'outer'), quarterSize, 0, quarterSize, quarterSize);
  outerContext.drawImage(resolveQuarterImage(atlasId, quarterSize, 'southWest', masks.southWest, 'outer'), 0, quarterSize, quarterSize, quarterSize);
  outerContext.drawImage(resolveQuarterImage(atlasId, quarterSize, 'southEast', masks.southEast, 'outer'), quarterSize, quarterSize, quarterSize, quarterSize);
  innerContext.drawImage(resolveQuarterImage(atlasId, quarterSize, 'northWest', masks.northWest, 'inner'), 0, 0, quarterSize, quarterSize);
  innerContext.drawImage(resolveQuarterImage(atlasId, quarterSize, 'northEast', masks.northEast, 'inner'), quarterSize, 0, quarterSize, quarterSize);
  innerContext.drawImage(resolveQuarterImage(atlasId, quarterSize, 'southWest', masks.southWest, 'inner'), 0, quarterSize, quarterSize, quarterSize);
  innerContext.drawImage(resolveQuarterImage(atlasId, quarterSize, 'southEast', masks.southEast, 'inner'), quarterSize, quarterSize, quarterSize, quarterSize);
  const composed = composePathTileLayers({
    atlasId,
    tileSize,
    outerMask: outerCanvas,
    innerMask: innerCanvas,
    textureImage,
    outerExpansionPx: getPathTileArtStyle(atlasId).outerExpansionPx,
    decorateInner: (target, mask) => {
      applyTileDecorations(
        atlasId,
        target,
        mask,
        `${atlasId}:${tileSize}:${masks.northWest}:${masks.northEast}:${masks.southWest}:${masks.southEast}`,
      );
    },
  });
  composedTileCache.set(cacheKey, composed);
  return composed;
}

export function composeSquarePathTile(
  atlasId: PathTileAtlasId,
  size: number,
  shape: SquarePathShape,
  textureImage?: HTMLImageElement,
): HTMLCanvasElement {
  return composeSquarePathTileFromCornerMasks(atlasId, size, cornerMasksFromShape(shape), textureImage);
}

export function composeSquarePathBrushPreview(
  atlasId: PathTileAtlasId,
  size: number,
  tileIndex: number,
  textureImage?: HTMLImageElement,
): HTMLCanvasElement {
  const shape = resolveSquarePathBrushShape(tileIndex) ?? EMPTY_SHAPE;
  const source = composeSquarePathTile(atlasId, size, shape, textureImage);
  const preview = createCanvas(size, size);
  const context = preview.getContext('2d');
  if (!context) {
    return preview;
  }
  context.imageSmoothingEnabled = false;
  context.drawImage(source, 0, 0);
  return preview;
}

export function composeSquarePathBrushAtlas(atlasId: PathTileAtlasId, tileSize: number, textureImage?: HTMLImageElement): HTMLCanvasElement {
  const hasTexture = textureImage && textureImage.complete;
  const cacheKey = `${atlasId}:${tileSize}:brush-atlas:${hasTexture ? 'tex' : 'notex'}`;
  const existing = brushAtlasCache.get(cacheKey);
  if (existing) {
    return existing;
  }
  const columns = 4;
  const rows = 4;
  const canvas = createCanvas(columns * tileSize, rows * tileSize);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }
  context.imageSmoothingEnabled = false;
  for (let tileIndex = 0; tileIndex < columns * rows; tileIndex += 1) {
    const tile = composeSquarePathBrushPreview(atlasId, tileSize, tileIndex, textureImage);
    const x = (tileIndex % columns) * tileSize;
    const y = Math.floor(tileIndex / columns) * tileSize;
    context.drawImage(tile, x, y);
  }
  brushAtlasCache.set(cacheKey, canvas);
  return canvas;
}

export function composeSquarePathQuarterAtlas(atlasId: PathTileAtlasId, tileSize: number, textureImage?: HTMLImageElement): HTMLCanvasElement {
  const hasTexture = textureImage && textureImage.complete;
  const cacheKey = `${atlasId}:${tileSize}:quarter-atlas:${hasTexture ? 'tex' : 'notex'}`;
  const existing = quarterAtlasCache.get(cacheKey);
  if (existing) {
    return existing;
  }
  const columns = 5;
  const canvas = createCanvas(columns * tileSize, tileSize);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }
  context.imageSmoothingEnabled = false;
  const archetypes: readonly SquareQuarterArchetype[] = [
    OUTER_CORNER_ARCHETYPE,
    STRAIGHT_ARCHETYPE,
    INNER_CORNER_ARCHETYPE,
    INVERSE_INNER_CORNER_ARCHETYPE,
    SOLID_ARCHETYPE,
  ];
  for (let tileIndex = 0; tileIndex < columns; tileIndex += 1) {
    context.drawImage(
      composeSquareQuarterPiece(atlasId, tileSize, archetypes[tileIndex] ?? SOLID_ARCHETYPE, textureImage),
      tileIndex * tileSize,
      0,
    );
  }
  quarterAtlasCache.set(cacheKey, canvas);
  return canvas;
}
